import React, { useEffect, useState } from "react";
import { Mail, Monitor, Send, Smartphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function AdminEmails() {
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [width, setWidth] = useState("desktop");

  useEffect(() => {
    api.get("/admin/email-previews")
      .then(({ data }) => {
        setTemplates(data.templates || []);
        if (data.templates?.length) setActive(data.templates[0].key);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    api.get(`/admin/email-previews/${active}`)
      .then(({ data }) => setPreview(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [active]);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/email-previews/${active}/test`);
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "The test email could not be sent.");
    } finally {
      setSending(false);
    }
  };

  const groups = templates.reduce((acc, t) => {
    acc[t.group] = acc[t.group] || [];
    acc[t.group].push(t);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="admin-emails-page">
      <div className="mb-8">
        <div className="eyebrow mb-2">Administration</div>
        <h1
          className="font-heading font-black text-4xl sm:text-5xl leading-none tracking-tight"
          style={{ color: "var(--ink)" }}
        >
          Email templates
        </h1>
        <p className="mt-3 text-base max-w-2xl" style={{ color: "var(--muted)" }}>
          Every email the portal sends, rendered with sample data so you can verify branding before
          clients receive it. Previews never touch real accounts.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <aside className="lg:col-span-1" data-testid="email-template-list">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group} className="mb-7">
              <div className="eyebrow mb-3">{group}</div>
              <div className="border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                {items.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setActive(t.key)}
                    className="w-full border-b px-4 py-3 text-left text-sm transition-colors last:border-b-0"
                    style={{
                      borderColor: "var(--border)",
                      background: active === t.key ? "var(--ink)" : "transparent",
                      color: active === t.key ? "#F7F7F6" : "var(--ink)",
                    }}
                    data-testid={`email-template-${t.key}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>

        <section className="lg:col-span-3" data-testid="email-preview-panel">
          <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="eyebrow flex items-center gap-2">
                <Mail size={13} /> Subject line
              </div>
              <p
                className="mt-1 font-heading text-2xl font-black leading-tight"
                style={{ color: "var(--ink)" }}
                data-testid="email-preview-subject"
              >
                {preview?.subject || "—"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWidth("desktop")}
                className="btn-ghost flex items-center gap-2"
                style={width === "desktop" ? { borderColor: "var(--ink)", color: "var(--ink)" } : {}}
                data-testid="email-preview-desktop"
              >
                <Monitor size={14} /> Desktop
              </button>
              <button
                onClick={() => setWidth("mobile")}
                className="btn-ghost flex items-center gap-2"
                style={width === "mobile" ? { borderColor: "var(--ink)", color: "var(--ink)" } : {}}
                data-testid="email-preview-mobile"
              >
                <Smartphone size={14} /> Mobile
              </button>
              <button
                onClick={sendTest}
                disabled={sending || !active}
                className="btn-primary flex items-center gap-2"
                data-testid="email-send-test"
              >
                {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                Send Test To Me
              </button>
            </div>
          </div>

          <div
            className="border p-6 flex justify-center"
            style={{ borderColor: "var(--border)", background: "#EDEDE8" }}
          >
            {loading || !preview ? (
              <p className="py-20 text-sm" style={{ color: "var(--muted)" }} data-testid="email-preview-loading">
                Rendering preview…
              </p>
            ) : (
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={preview.preview_html || preview.html}
                className="border-0 bg-white"
                style={{ width: width === "mobile" ? 390 : "100%", maxWidth: "100%", height: 760 }}
                data-testid="email-preview-frame"
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
